<template>
  <!-- Light/dark switch. The page follows the device until the visitor picks a
       theme; the pick is remembered, and picking the device's own theme clears
       it. Styles: _theme-switch.scss. -->
  <button
    :class="['theme-sw', 'theme-sw-' + look, { dark: isDark, quiet }]"
    type="button"
    role="switch"
    aria-label="Dark mode"
    :aria-checked="String(isDark)"
    @click="toggle"
    @mouseleave="quiet = false"
    @blur="quiet = false"
  >
    <span class="theme-sw-pill">
      <span class="theme-sw-knob">
        <!-- Keyed so the icon's entry animation replays on each switch. -->
        <svg :key="theme" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path v-if="isDark" d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          <template v-else>
            <circle cx="12" cy="12" r="4.2" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </template>
        </svg>
      </span>
      <span v-if="look === 'bevel'" class="theme-sw-text"><span>{{ isDark ? "Dark mode" : "Light mode" }}</span></span>
    </span>
  </button>
</template>

<script>
import { RESOLVED_ATTR } from "./theme";

// The stored pick is "light" or "dark"; with none, the device decides.
const STORAGE_KEY = "landing_theme";
// "flat": an outlined icon button like the nav's "Log in" (in use).
// "bevel": a 3D pill whose "Light mode / Dark mode" label shows on hover.
const LOOK = "flat";

export default {
  name: "ThemeToggle",
  data() {
    return {
      look: LOOK,
      systemDark: false,
      choice: null,
      // Hides the bevel's hover label after a click, until the pointer leaves.
      quiet: false,
    };
  },
  computed: {
    theme() {
      return this.choice || (this.systemDark ? "dark" : "light");
    },
    isDark() {
      return this.theme === "dark";
    },
  },
  watch: {
    theme: "apply",
  },
  created() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "light" || saved === "dark") this.choice = saved;
    } catch (e) {
      // Storage can be unavailable in private windows; the device decides.
    }

    this.media = window.matchMedia("(prefers-color-scheme: dark)");
    this.systemDark = this.media.matches;
    // Safari before 14 only has addListener / removeListener.
    if (this.media.addEventListener) this.media.addEventListener("change", this.onSystemChange);
    else this.media.addListener(this.onSystemChange);
    this.apply();
  },
  beforeDestroy() {
    if (this.media.removeEventListener) this.media.removeEventListener("change", this.onSystemChange);
    else this.media.removeListener(this.onSystemChange);
    document.documentElement.removeAttribute(RESOLVED_ATTR);
  },
  methods: {
    onSystemChange(e) {
      this.systemDark = e.matches;
    },
    toggle() {
      const next = this.isDark ? "light" : "dark";
      this.choice = next === (this.systemDark ? "dark" : "light") ? null : next;
      this.quiet = true;
      try {
        if (this.choice) localStorage.setItem(STORAGE_KEY, this.choice);
        else localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // The pick just won't be remembered.
      }
    },
    apply() {
      document.documentElement.setAttribute(RESOLVED_ATTR, this.theme);
    },
  },
};
</script>
